import React, { useEffect, useState } from "react";
import { pdf } from "@react-pdf/renderer";
import { FileDown } from "lucide-react";
import { shopApi, formatShopAuthError } from "../../lib/shopAuth";
import { buildInvoicePdfBlob } from "../../lib/invoiceQr";
import CreditNotePdf, { REASON_LABEL } from "../CreditNotePdf";
import { BLDocument } from "../../pages/DeliveryNoteDetail";
import { COLORS, fmtCHF, fmtDay, openPdf, Panel, Loading, Empty, Tag, GhostButton } from "./ui";

const INV_STATUS = {
  ENVOYEE: ["À payer", COLORS.gold], PARTIELLE: ["Partiellement payée", COLORS.gold],
  EN_RETARD: ["En retard", COLORS.chili], PAYEE: ["Payée", COLORS.basil],
};
const BL_STATUS = { ENVOYE: ["Envoyé", COLORS.gold], LIVRE: ["Livré", COLORS.basil], SIGNE: ["Signé", COLORS.basil] };
const NC_STATUS = { ENVOYEE: ["Émis", COLORS.gold], EXECUTEE: ["Émis", COLORS.gold] };
const ORDER_STATUS = {
  NOUVELLE: "Nouvelle", VALIDEE: "Validée", EN_PRODUCTION: "En production", PRETE: "Prête",
  LIVREE: "Livrée", FACTUREE: "Facturée", PAYEE: "Payée", PAIEMENT_EN_ATTENTE: "Paiement en attente",
};

const useList = (path) => {
  const [rows, setRows] = useState(null);
  useEffect(() => { shopApi.get(path).then(({ data }) => setRows(data || [])).catch(() => setRows([])); }, [path]);
  return rows;
};

const Row = ({ testid, title, tags, meta, amount, action }) => (
  <li className="p-4 md:p-5 flex items-center justify-between gap-4 flex-wrap" data-testid={testid}>
    <div className="min-w-0 flex-1">
      <div className="flex items-center gap-2 flex-wrap">
        <p className="text-sm font-mono" style={{ color: COLORS.primary }}>{title}</p>
        {tags}
      </div>
      <p className="text-xs mt-1" style={{ color: COLORS.muted }}>{meta}</p>
    </div>
    <div className="flex items-center gap-4 shrink-0">
      {amount != null && <p className="font-mono text-sm" style={{ color: COLORS.gold }}>{amount}</p>}
      {action}
    </div>
  </li>
);

const List = ({ rows, empty, testid, children }) => {
  if (rows === null) return <Loading />;
  if (!rows.length) return <Empty testid={`${testid}-empty`}>{empty}</Empty>;
  return (
    <Panel testid={testid} className="!p-0">
      <ul className="divide-y" style={{ borderColor: "rgba(242,236,228,0.08)" }}>{children}</ul>
    </Panel>
  );
};

const PdfButton = ({ testid, load }) => {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const run = async () => {
    setBusy(true); setErr("");
    try { const [blob, name] = await load(); openPdf(blob, name); }
    catch (e) { setErr(formatShopAuthError(e.response?.data?.detail) || e.message || "Erreur PDF"); }
    finally { setBusy(false); }
  };
  return (
    <div className="flex flex-col items-end">
      <GhostButton onClick={run} loading={busy} icon={FileDown} testid={testid}>PDF</GhostButton>
      {err && <span className="text-[10px] mt-1" style={{ color: COLORS.chili }}>{err}</span>}
    </div>
  );
};

export const ResellerInvoices = () => {
  const rows = useList("/my-reseller/invoices");
  const load = (id) => async () => {
    const { data } = await shopApi.get(`/my-reseller/invoices/${id}`);
    return [await buildInvoicePdfBlob(data.invoice, data.settings), `Facture-${data.invoice.reference}.pdf`];
  };
  return (
    <List rows={rows} testid="pro-invoices" empty="Aucune facture émise pour le moment.">
      {(rows || []).map((i) => {
        const [label, color] = INV_STATUS[i.status] || [i.status, COLORS.muted];
        const due = (Number(i.total_ttc) || 0) - (Number(i.paid_amount) || 0);
        return (
          <Row key={i.id} testid={`pro-invoice-${i.id}`} title={i.reference} tags={<Tag color={color}>{label}</Tag>}
            meta={<>Émise le {fmtDay(i.issue_date)}{i.due_date && <> · échéance {fmtDay(i.due_date)}</>}{i.status !== "PAYEE" && due > 0.004 && <> · reste {fmtCHF(due)} CHF</>}</>}
            amount={`${fmtCHF(i.total_ttc)} CHF`} action={<PdfButton testid={`pro-invoice-pdf-${i.id}`} load={load(i.id)} />} />
        );
      })}
    </List>
  );
};

export const ResellerDeliveryNotes = () => {
  const rows = useList("/my-reseller/delivery-notes");
  const load = (id) => async () => {
    const { data } = await shopApi.get(`/my-reseller/delivery-notes/${id}`);
    const blob = await pdf(<BLDocument bl={data.delivery_note} settings={data.settings} showPrices />).toBlob();
    return [blob, `BL-${data.delivery_note.reference}.pdf`];
  };
  return (
    <List rows={rows} testid="pro-bls" empty="Aucun bon de livraison pour le moment.">
      {(rows || []).map((b) => {
        const [label, color] = BL_STATUS[b.status] || [b.status, COLORS.muted];
        return (
          <Row key={b.id} testid={`pro-bl-${b.id}`} title={b.reference}
            tags={<>{<Tag color={color}>{label}</Tag>}{b.invoice_id && <Tag color={COLORS.basil}>Facturé</Tag>}</>}
            meta={<>Livraison du {fmtDay(b.delivery_date)}{b.distributor_name && <> · {b.distributor_name}</>}</>}
            action={<PdfButton testid={`pro-bl-pdf-${b.id}`} load={load(b.id)} />} />
        );
      })}
    </List>
  );
};

export const ResellerOrders = () => {
  const rows = useList("/my-reseller/orders");
  return (
    <List rows={rows} testid="pro-orders" empty="Aucune commande pour le moment.">
      {(rows || []).map((o) => (
        <Row key={o.id} testid={`pro-order-${o.id}`} title={o.reference}
          tags={<>{o.is_web && <Tag>En ligne</Tag>}{o.bl_id && <Tag color={COLORS.basil}>BL</Tag>}{o.invoice_id && <Tag color={COLORS.basil}>Facturée</Tag>}</>}
          meta={<>{fmtDay(o.created_at)} · {o.items_count} article(s) · <span style={{ color: COLORS.secondary }}>{ORDER_STATUS[o.status] || o.status}</span></>}
          amount={`${fmtCHF(o.total_ttc)} CHF`} />
      ))}
    </List>
  );
};

export const ResellerCreditNotes = () => {
  const rows = useList("/my-reseller/credit-notes");
  const load = (id) => async () => {
    const { data } = await shopApi.get(`/my-reseller/credit-notes/${id}`);
    const blob = await pdf(<CreditNotePdf nc={data.credit_note} settings={data.settings} />).toBlob();
    return [blob, `Avoir-${data.credit_note.reference}.pdf`];
  };
  return (
    <List rows={rows} testid="pro-credit-notes" empty="Aucun avoir pour le moment.">
      {(rows || []).map((n) => {
        const [label, color] = NC_STATUS[n.status] || [n.status, COLORS.muted];
        const remaining = (Number(n.total_ttc) || 0) - (Number(n.applied_total) || 0) - (Number(n.refunded_amount) || 0);
        return (
          <Row key={n.id} testid={`pro-nc-${n.id}`} title={n.reference}
            tags={<>{<Tag color={color}>{label}</Tag>}{remaining <= 0.004 ? <Tag color={COLORS.basil}>Utilisé</Tag> : <Tag>Disponible {fmtCHF(remaining)}</Tag>}</>}
            meta={<>{fmtDay(n.issue_date)} · {REASON_LABEL[n.reason] || n.reason}{n.source_invoice_reference && <> · facture {n.source_invoice_reference}</>}</>}
            amount={`-${fmtCHF(n.total_ttc)} CHF`} action={<PdfButton testid={`pro-nc-pdf-${n.id}`} load={load(n.id)} />} />
        );
      })}
    </List>
  );
};
